<!-- Shared v2 application header. Retains the legacy header's props and slots,
  with an additional brand slot. Requires the shared v2 theme tokens;
  adopting apps import the theme separately. -->
<template lang="pug">
nav.navbar-v2#header
  router-link.brand(to='/')
    slot(name="brand")
      img(style="height: 30px; width: auto;" src="@stl/assets/images/philips_shape_large.png")
      span.name Philips
      span.sep
      span.product {{ title }}
  slot(name="after-title")
  .header-spacer
  slot(name="actions-before")
  .theme-toggle(role="group" aria-label="Theme")
    button(type="button" :class="{ 'is-active': theme.isLight }" aria-label="Light theme" @click="theme.setLight")
      FontAwesomeIcon(icon="sun")
    button(type="button" :class="{ 'is-active': theme.isDark }" aria-label="Dark theme" @click="theme.setDark")
      FontAwesomeIcon(icon="moon")
  slot(name="actions-after")

  b-dropdown.user-menu(v-if="user" variant="link" no-caret)
    template(#button-content)
      span.avatar {{ initials }}
      span.username {{ user && user.username }}
      FontAwesomeIcon.chevron(icon="angle-down")
    slot(name="user-menu")
    //- Workaround: BLink always uses RouterLink when a router is present, preventing external navigation.
    template(v-if="logoutUrl")
      a.dropdown-item(:href="logoutUrl")
        FontAwesomeIcon.me-2(icon="lock")
        | Logout
    template(v-else)
      ActionMenuItem(:to="toLogout", icon="lock") Logout
</template>

<script setup lang="ts">
import { useTheme } from '../../stores/theme';
import { computed } from 'vue';
import ActionMenuItem from '../../components/ActionMenuItem.vue';

const theme = useTheme();
interface User {
  email: string;
  username: string;
}

const props = defineProps<{
  title: string;
  user: User | null;
  toLogout?: Object;
  logoutUrl?: string;
}>();

let logoutUrl = computed(() => {
  if (props.logoutUrl) {
    return props.logoutUrl;
  }
  return '/auth/logout';
});

const initials = computed(() => {
  const name = props.user?.username?.trim();
  if (!name) {
    return '?';
  }
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
});
</script>

<style lang="scss" scoped>
.navbar-v2 {
  height: 60px;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 0 12px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  text-decoration: none;

  img {
    height: 30px;
    width: 30px;
    border-radius: 7px;
    display: block;
  }

  .name {
    font-weight: 600;
    font-size: 15px;
    letter-spacing: -0.01em;
    color: var(--fg);
  }

  .sep {
    width: 1px;
    height: 20px;
    background: var(--border);
  }

  .product {
    font-size: 13px;
    color: var(--muted);
    font-weight: 510;
  }
}

.header-spacer {
  flex: 1;
}

.theme-toggle {
  display: flex;
  align-items: center;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 3px;
  gap: 2px;

  button {
    width: 30px;
    height: 26px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--muted);
    display: grid;
    place-items: center;
    cursor: pointer;
    transition:
      background 0.15s,
      color 0.15s;

    &.is-active {
      background: var(--surface);
      color: var(--fg);
      box-shadow: var(--shadow);
    }

    svg {
      width: 15px;
      height: 15px;
    }
  }
}

.user-menu {
  :deep(.btn) {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px;
    border-radius: 8px;
    color: var(--fg);
    font-size: 13px;
    font-weight: 510;
    text-decoration: none;

    &:hover {
      background: var(--surface-2);
    }
  }
}

.avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent-deep);
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  flex: 0 0 auto;
}

.chevron {
  width: 11px;
  height: 11px;
  color: var(--muted);
}
</style>
